import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Clock, Flame, Loader2, Save, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function Aquecimento() {
  const { user, refresh } = useAuth();
  const [chips, setChips] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [togglingAll, setTogglingAll] = useState(false);
  const [schedule, setSchedule] = useState({ enabled: false, start: "08:00", end: "22:30" });
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    if (user) {
      setSchedule({
        enabled: !!user.schedule_enabled,
        start: user.schedule_start || "08:00",
        end: user.schedule_end || "22:30",
      });
    }
  }, [user]);

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put("/settings/schedule", { enabled: schedule.enabled, start: schedule.start, end: schedule.end });
      toast.success(schedule.enabled ? `Agenda ativada: ${schedule.start} às ${schedule.end}` : "Agenda padrão (08:00–22:30) restaurada");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingSchedule(false);
    }
  };

  const fetchChips = useCallback(async () => {
    try {
      const { data } = await api.get("/chips");
      setChips(data);
      const d = {};
      data.forEach((c) => {
        d[c.id] = { delay_min: c.delay_min, delay_max: c.delay_max, simulate_typing: c.simulate_typing };
      });
      setDrafts(d);
    } catch {}
  }, []);

  useEffect(() => {
    fetchChips();
  }, [fetchChips]);

  const connected = chips.filter((c) => c.status === "conectado");
  const allOn = connected.length > 0 && connected.every((c) => c.warmup_enabled);

  const toggleAll = async () => {
    setTogglingAll(true);
    try {
      await api.post("/chips/warmup-all", { enabled: !allOn });
      toast.success(!allOn ? "Loop global ativado para todos os chips" : "Loop global pausado");
      fetchChips();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTogglingAll(false);
    }
  };

  const toggleWarmup = async (chip) => {
    try {
      await api.patch(`/chips/${chip.id}/config`, { warmup_enabled: !chip.warmup_enabled });
      fetchChips();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const save = async (chip) => {
    const d = drafts[chip.id];
    if (!d) return;
    if (Number(d.delay_max) < Number(d.delay_min)) {
      toast.error("O delay máximo deve ser maior ou igual ao mínimo");
      return;
    }
    setSaving(chip.id);
    try {
      await api.patch(`/chips/${chip.id}/config`, {
        delay_min: Number(d.delay_min),
        delay_max: Number(d.delay_max),
        simulate_typing: d.simulate_typing,
      });
      toast.success(`Configuração de ${chip.apelido} salva`);
      fetchChips();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="aquecimento-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Painel de Aquecimento</h1>
        <p className="text-sm text-slate-400 mt-1">Controle o robô de cada chip e ajuste delays que simulam comportamento humano.</p>
      </div>

      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <Flame className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="font-semibold text-slate-100">Loop global de maturação</p>
            <p className="text-xs text-slate-500">
              {connected.length === 0
                ? "Conecte chips no Dashboard para ativar"
                : allOn
                ? "Todos os chips conectados estão aquecendo"
                : "Um ou mais chips estão pausados"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {togglingAll && <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />}
          <Switch checked={allOn} onCheckedChange={toggleAll} disabled={connected.length === 0 || togglingAll} data-testid="warmup-master-toggle" />
        </div>
      </div>

      <div className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6" data-testid="warmup-schedule-card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="font-semibold text-slate-100">Agenda do loop de aquecimento</p>
              <p className="text-xs text-slate-500">
                {schedule.enabled
                  ? `Personalizada: ativo das ${schedule.start} às ${schedule.end} (horário de Brasília)`
                  : "Padrão: ativo das 08:00 às 22:30 (horário de Brasília)"}
              </p>
            </div>
          </div>
          <Switch checked={schedule.enabled} onCheckedChange={(v) => setSchedule((s) => ({ ...s, enabled: v }))} data-testid="warmup-schedule-toggle" />
        </div>
        {schedule.enabled && (
          <div className="grid grid-cols-2 gap-4 mt-5">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Início</label>
              <Input type="time" value={schedule.start} onChange={(e) => setSchedule((s) => ({ ...s, start: e.target.value }))}
                data-testid="warmup-schedule-start"
                className="bg-slate-800/60 border-slate-700 text-slate-100 font-mono focus-visible:ring-2 focus-visible:ring-emerald-500" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Fim</label>
              <Input type="time" value={schedule.end} onChange={(e) => setSchedule((s) => ({ ...s, end: e.target.value }))}
                data-testid="warmup-schedule-end"
                className="bg-slate-800/60 border-slate-700 text-slate-100 font-mono focus-visible:ring-2 focus-visible:ring-emerald-500" />
            </div>
          </div>
        )}
        <Button onClick={saveSchedule} disabled={savingSchedule} data-testid="warmup-schedule-save"
          className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold transition-colors duration-150">
          {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" /> Salvar agenda</>)}
        </Button>
      </div>

      {connected.length === 0 ? (
        <div className="bg-[#161F32] border border-dashed border-slate-700 rounded-xl p-10 text-center" data-testid="aquecimento-empty-state">
          <Smartphone className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-300 font-medium">Nenhum chip conectado</p>
          <p className="text-sm text-slate-500">Conecte chips pelo Dashboard para configurar o aquecimento.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {connected.map((chip) => {
            const d = drafts[chip.id] || {};
            return (
              <div key={chip.id} className="bg-[#161F32] border border-slate-800 rounded-xl p-5 sm:p-6" data-testid={`warmup-chip-card-${chip.id}`}>
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={chip.avatar} alt={chip.apelido} className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700" />
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-100 truncate">{chip.apelido}</p>
                      <p className="text-xs text-slate-500 font-mono">{chip.number}</p>
                      {chip.health != null && (
                        <p className={`text-[10px] font-mono font-semibold mt-0.5 ${chip.health >= 80 ? "text-emerald-400" : chip.health >= 50 ? "text-amber-400" : "text-red-400"}`}
                          data-testid={`warmup-chip-health-${chip.id}`}>
                          Saúde do chip: {chip.health}%
                        </p>
                      )}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-400 shrink-0">
                    <span>{chip.warmup_enabled ? "Aquecendo" : "Pausado"}</span>
                    <Switch checked={chip.warmup_enabled} onCheckedChange={() => toggleWarmup(chip)} data-testid={`warmup-chip-toggle-${chip.id}`} />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Delay mínimo (s)</label>
                    <Input
                      type="number"
                      min={10}
                      max={3600}
                      value={d.delay_min ?? ""}
                      onChange={(e) => setDrafts((p) => ({ ...p, [chip.id]: { ...d, delay_min: e.target.value } }))}
                      data-testid={`warmup-delay-min-input-${chip.id}`}
                      className="bg-slate-800/60 border-slate-700 text-slate-100 font-mono focus-visible:ring-2 focus-visible:ring-emerald-500"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Delay máximo (s)</label>
                    <Input
                      type="number"
                      min={10}
                      max={7200}
                      value={d.delay_max ?? ""}
                      onChange={(e) => setDrafts((p) => ({ ...p, [chip.id]: { ...d, delay_max: e.target.value } }))}
                      data-testid={`warmup-delay-max-input-${chip.id}`}
                      className="bg-slate-800/60 border-slate-700 text-slate-100 font-mono focus-visible:ring-2 focus-visible:ring-emerald-500"
                    />
                  </div>
                </div>

                <label className="flex items-center justify-between gap-3 bg-slate-900/60 border border-slate-800 rounded-lg p-3 mb-4 text-sm text-slate-300">
                  <span>Simular "Digitando..." por 2 a 5 segundos</span>
                  <Switch
                    checked={!!d.simulate_typing}
                    onCheckedChange={(v) => setDrafts((p) => ({ ...p, [chip.id]: { ...d, simulate_typing: v } }))}
                    data-testid={`warmup-typing-toggle-${chip.id}`}
                  />
                </label>

                <Button
                  onClick={() => save(chip)}
                  disabled={saving === chip.id}
                  data-testid={`warmup-save-settings-button-${chip.id}`}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold transition-colors duration-150"
                >
                  {saving === chip.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" /> Salvar configuração</>)}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
